import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, TrendingUp, TrendingDown, ArrowUpDown } from 'lucide-react';
import { useMobile } from '../../hooks/useMobile';

const InvestmentTableView = ({ 
  investments, 
  visibleFields, 
  onDelete,
  canEdit,
  canDelete,
  currencyFormat = 'full',
  selectedCurrency = 'INR',
  exchangeRate = 83.5
}) => {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const [sortField, setSortField] = useState('portfolio_company_name');
  const [sortDirection, setSortDirection] = useState('asc');

  const convertToDisplayCurrency = (amount, sourceCurrency) => {
    if (amount == null) return 0;
    const src = (sourceCurrency || 'INR').toUpperCase();
    const tgt = selectedCurrency.toUpperCase();
    if (src === tgt) return amount;
    if (src === 'INR' && tgt === 'USD') return amount / exchangeRate;
    if (src === 'USD' && tgt === 'INR') return amount * exchangeRate;
    return amount;
  };

  const formatCurrency = (amount, format = 'full', sourceCurrency = 'INR') => {
    if (!amount && amount !== 0) return '-';
    
    // Convert amount based on source currency vs selected display currency
    const displayAmount = convertToDisplayCurrency(amount, sourceCurrency);
    
    const currencySymbol = selectedCurrency === 'INR' ? '₹' : '$';
    const locale = selectedCurrency === 'INR' ? 'en-IN' : 'en-US';
    
    switch (format) {
      case 'crores':
        return `${currencySymbol}${(displayAmount / 10000000).toFixed(2)}Cr`;
      case 'millions':
        return `${currencySymbol}${(displayAmount / 1000000).toFixed(2)}M`;
      case 'full':
      default:
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: selectedCurrency,
          maximumFractionDigits: 0
        }).format(displayAmount);
    }
  };

  const formatPercentage = (value) => {
    if (!value && value !== 0) return '-';
    return `${value.toFixed(2)}%`;
  };

  const formatMultiple = (value) => {
    if (!value && value !== 0) return '-';
    return `${value.toFixed(2)}x`;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedInvestments = [...investments].sort((a, b) => {
    let aVal = a[sortField] || a.metrics?.[sortField];
    let bVal = b[sortField] || b.metrics?.[sortField];
    
    if (aVal === undefined) aVal = '';
    if (bVal === undefined) bVal = '';
    
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = (bVal || '').toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const fieldConfig = {
    portfolio_company_name: { label: 'Company Name', type: 'text', sortable: true },
    investment_stage: { label: 'Stage', type: 'badge', sortable: true },
    sector: { label: 'Sector', type: 'text', sortable: true },
    investment_currency: { label: 'Currency', type: 'text', sortable: true },
    types_of_securities_offered: { label: 'Securities', type: 'text', sortable: true },
    total_investment: { label: 'Total Invested', type: 'currency', sortable: true },
    total_realisation: { label: 'Total Realisation', type: 'currency', sortable: true },
    current_stake_value: { label: 'Current Stake Value', type: 'currency', sortable: true },
    current_shareholding: { label: '% Shareholding', type: 'percentage', sortable: true },
    multiple: { label: 'Multiple (MOIC)', type: 'multiple', sortable: true },
    irr: { label: 'IRR %', type: 'percentage', sortable: true },
    created_at: { label: 'Created Date', type: 'date', sortable: true }
  };

  const renderCellValue = (investment, field) => {
    const config = fieldConfig[field];
    let value = investment[field];
    
    // Check if it's a metric field
    if (['total_investment', 'total_realisation', 'current_stake_value', 'current_shareholding', 'multiple', 'irr'].includes(field)) {
      value = investment.metrics?.[field];
    }
    
    if (!config) return value || '-';
    
    switch (config.type) {
      case 'currency':
        return formatCurrency(value, currencyFormat, investment.investment_currency || 'INR');
      case 'percentage':
        return formatPercentage(value);
      case 'multiple':
        return formatMultiple(value);
      case 'badge':
        return (
          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
            value === 'Active' ? 'bg-green-100 text-green-800' :
            value === 'Exited' ? 'bg-gray-100 text-gray-800' :
            value === 'Pipeline' ? 'bg-blue-100 text-blue-800' :
            'bg-yellow-100 text-yellow-800'
          }`}>
            {value || 'Pipeline'}
          </span>
        );
      case 'date':
        return value ? new Date(value).toLocaleDateString() : '-';
      default:
        return value || '-';
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="opacity-30" />;
    return sortDirection === 'asc' ? 
      <TrendingUp size={14} /> : 
      <TrendingDown size={14} />;
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {visibleFields.map((field) => {
                const config = fieldConfig[field];
                return (
                  <th
                    key={field}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => config.sortable && handleSort(field)}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{config.label}</span>
                      {config.sortable && <SortIcon field={field} />}
                    </div>
                  </th>
                );
              })}
              {!isMobile && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedInvestments.length === 0 ? (
              <tr>
                <td colSpan={visibleFields.length + 1} className="px-6 py-12 text-center text-gray-500">
                  No investments found
                </td>
              </tr>
            ) : (
              sortedInvestments.map((investment) => (
                <tr 
                  key={investment.id} 
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/investments/${investment.id}`)}
                >
                  {visibleFields.map((field) => (
                    <td key={field} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {renderCellValue(investment, field)}
                    </td>
                  ))}
                  {!isMobile && (
                    <td 
                      className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end space-x-2">
                        {canDelete && (
                          <button
                            onClick={() => onDelete(investment.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvestmentTableView;
